import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import { addTag, archiveEntry, createReply, deleteAttachment, downloadAttachment, downloadEntry, getEntry, listParents, listReplies, removeTag, unarchiveEntry, updateEntry, updateTodoStatus, updateTodoAssignee } from '../api/entries'
import type { AttachmentInfo, Entry } from '../api/entries'
import { AuthenticatedMedia } from '../components/Entry/AuthenticatedMedia'
import { EntryCard } from '../components/Entry/EntryCard'
import { EntryEditForm } from '../components/Entry/EntryEditForm'
import { TagInput } from '../components/Entry/TagInput'
import type { ColorTheme } from '../components/Entry/colorTheme'
import { MarkdownRenderer } from '../components/Entry/MarkdownRenderer'
import { AvatarImage } from '../components/AvatarImage'
import { useAuthStore } from '../store/auth'
import { useDefaultsStore } from '../store/defaults'
import { formatDateTime } from '../utils/dates'

const PRIVACY_LABELS = ['private', 'friends', 'friends-of-friends', 'public']

export function EntryView() {
  const params = useParams()
  const entryId = params['*']
  const [entry, setEntry] = useState<Entry | null>(null)
  const [error, setError] = useState('')
  const currentUser = useAuthStore((s) => s.username)
  const emailVerified = useAuthStore((s) => s.emailVerified)
  const emailEnabled = useAuthStore((s) => s.emailEnabled)
  const { defaults } = useDefaultsStore()
  const [tags, setTags] = useState<{ name: string; tagger_username: string }[]>([])

  const [attachments, setAttachments] = useState<AttachmentInfo[]>([])
  const [downloading, setDownloading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editContent, setEditContent] = useState('')
  const [editPrivacy, setEditPrivacy] = useState(0)
  const [editAllowReplies, setEditAllowReplies] = useState(false)
  const [editColorTheme, setEditColorTheme] = useState<ColorTheme | null>(null)
  const [saving, setSaving] = useState(false)

  const [todoEditingAssignee, setTodoEditingAssignee] = useState(false)
  const [todoAssigneeInput, setTodoAssigneeInput] = useState('')

  const [parents, setParents] = useState<Entry[]>([])
  const [replies, setReplies] = useState<Entry[]>([])
  const [replying, setReplying] = useState(false)
  const [replyName, setReplyName] = useState('')
  const [replyContent, setReplyContent] = useState('')
  const [replyPrivacy, setReplyPrivacy] = useState(() => defaults.reply)
  const [replyColorTheme, setReplyColorTheme] = useState<ColorTheme | null>(null)
  const [submittingReply, setSubmittingReply] = useState(false)

  const editIsDirty = editing && (
    editName !== (entry?.name ?? '') ||
    editContent !== (entry?.content ?? '') ||
    editPrivacy !== (entry?.privacy ?? 0) ||
    editAllowReplies !== (entry?.allow_replies ?? false) ||
    JSON.stringify(editColorTheme) !== JSON.stringify(
      entry?.color_theme
        ? { bg: entry.color_theme.bg, fg: entry.color_theme.fg, color1: entry.color_theme.color1, color2: entry.color_theme.color2 }
        : null
    )
  )
  const isDirty = editIsDirty || (replying && (replyName !== '' || replyContent !== ''))
  useUnsavedChangesGuard(isDirty)

  useEffect(() => {
    if (!entryId) return
    getEntry(entryId)
      .then((e) => { setEntry(e); setTags(e.tags); setAttachments(e.attachments ?? []) })
      .catch((e) => setError(e.message))
  }, [entryId])

  useEffect(() => {
    if (!entry) return
    listReplies(entry.id).then(setReplies).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.id])

  useEffect(() => {
    if (!entry || entry.entry_type !== 'todo') return
    listParents(entry.id).then(setParents).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.id])

  const autoEditRef = useRef(false)
  useEffect(() => {
    if (!entry || autoEditRef.current) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('edit') !== '1') return
    autoEditRef.current = true
    startEdit()
    const url = new URL(window.location.href)
    url.searchParams.delete('edit')
    window.history.replaceState({}, '', url.toString())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.id])

  const handleMarkdownUpdate = useCallback(
    (newContent: string) => setEntry((e) => e ? { ...e, content: newContent } : e),
    [],
  )

  if (error) return <p style={{ color: 'var(--color-1)' }}>{error}</p>
  if (!entry) return <p>loading…</p>

  const isOwner = currentUser === entry.username
  const date = formatDateTime(entry.created_at)
  const isEdited = (entry.version_num ?? 0) > 1

  async function handleAddTag(tag: string) {
    try {
      await addTag(entry!.id, tag)
      setTags((prev) => prev.some((t) => t.name === tag) ? prev : [...prev, { name: tag, tagger_username: currentUser! }])
    } catch { /* ignore */ }
  }

  async function handleRemoveTag(tag: string) {
    try {
      await removeTag(entry!.id, tag)
      setTags((prev) => prev.filter((t) => t.name !== tag))
    } catch { /* ignore */ }
  }

  async function handleTodoStatusChange(status: string) {
    try {
      const updated = await updateTodoStatus(entry!.id, status)
      setEntry(updated)
    } catch { /* ignore */ }
  }

  async function submitTodoAssignee(e: React.FormEvent) {
    e.preventDefault()
    try {
      const updated = await updateTodoAssignee(entry!.id, todoAssigneeInput.trim() || null)
      setEntry(updated)
      setTodoEditingAssignee(false)
    } catch { /* ignore */ }
  }

  function startEdit() {
    setEditName(entry!.name)
    setEditContent(entry!.content ?? '')
    setEditPrivacy(entry!.privacy)
    setEditAllowReplies(entry!.allow_replies)
    const ct = entry!.color_theme
    setEditColorTheme(ct ? { bg: ct.bg, fg: ct.fg, color1: ct.color1, color2: ct.color2 } : null)
    setEditing(true)
  }

  async function handleDeleteEditAttachment(id: string) {
    await deleteAttachment(entry!.id, id).catch(() => {})
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  async function handleArchive() {
    if (!entry || !window.confirm(`archive "${entry.name || 'untitled'}"?`)) return
    try {
      const updated = await archiveEntry(entry.id)
      setEntry(updated)
    } catch { /* ignore */ }
  }

  async function handleUnarchive() {
    if (!entry) return
    try {
      const updated = await unarchiveEntry(entry.id)
      setEntry(updated)
    } catch { /* ignore */ }
  }

  async function submitEdit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const updated = await updateEntry(entry!.id, {
        name: editName,
        content: editContent,
        privacy: editPrivacy,
        allow_replies: editAllowReplies,
        color_theme: editColorTheme,
      })
      setEntry(updated)
      setTags(updated.tags)
      setEditing(false)
    } catch { /* ignore */ }
    finally { setSaving(false) }
  }

  async function handleSubmitReply(e: React.FormEvent) {
    e.preventDefault()
    if (!replyName.trim()) return
    setSubmittingReply(true)
    try {
      const reply = await createReply(entry!.id, {
        name: replyName.trim(),
        content: replyContent,
        mimetype: 'text/markdown',
        privacy: replyPrivacy,
        color_theme: replyColorTheme ?? undefined,
      })
      setReplies((prev) => [...prev, reply])
      setReplyName('')
      setReplyContent('')
      setReplyPrivacy(0)
      setReplyColorTheme(null)
      setReplying(false)
    } catch { /* ignore */ }
    finally { setSubmittingReply(false) }
  }

  const ct = entry.color_theme
  const themeVars: React.CSSProperties = ct
    ? ({
        '--color-bg': ct.bg,
        '--color-fg': ct.fg,
        '--color-1': ct.color1,
        '--color-2': ct.color2,
      } as React.CSSProperties)
    : {}
  const bodyStyle: React.CSSProperties = ct
    ? { background: ct.bg, color: ct.fg, padding: '1rem', borderRadius: 2, margin: '0.75rem 0' }
    : { margin: '0.75rem 0' }

  return (
    <article style={themeVars}>
      {isOwner && entry.archived_at && (
        <div style={{ background: 'var(--color-fg)', color: 'var(--color-bg)', padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.85rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <span>this entry is archived.</span>
          <button
            type="button"
            onClick={handleUnarchive}
            style={{ display: 'inline', width: 'auto', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', color: 'inherit', padding: 0, fontSize: 'inherit' }}
          >
            restore
          </button>
        </div>
      )}

      {editing ? (
        <EntryEditForm
          name={editName} onNameChange={setEditName}
          content={editContent} onContentChange={setEditContent}
          contentRows={14} grow
          privacy={editPrivacy} onPrivacyChange={setEditPrivacy}
          colorTheme={editColorTheme} onColorThemeChange={setEditColorTheme}
          username={currentUser}
          allowReplies={editAllowReplies} onAllowRepliesChange={setEditAllowReplies}
          saving={saving}
          onSubmit={submitEdit}
          onCancel={() => setEditing(false)}
          style={{ marginBottom: '1.5rem' }}
          existingAttachments={attachments}
          onDeleteExistingAttachment={handleDeleteEditAttachment}
          showAttachButton
          tags={tags}
          onAddTag={currentUser ? handleAddTag : undefined}
          onRemoveTag={currentUser ? handleRemoveTag : undefined}
          onArchive={isOwner && !entry.archived_at ? handleArchive : undefined}
        />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0.5rem 0' }}>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0 }}>{entry.name || <em>untitled</em>}</h1>
              <div style={{ display: 'flex', alignItems: 'center', opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {entry.avatar_entry_id && <AvatarImage entryId={entry.avatar_entry_id} size={20} />}
                  <Link to={`/~${entry.username}`}>~{entry.username}</Link>
                  {' · '}
                  {date}
                  {isEdited && <>{' · '}<span title={formatDateTime(entry.updated_at)}>edited</span></>}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem', fontSize: '0.8rem', flexShrink: 0 }}>
              {entry.entry_type === 'todo' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.7 }}>
                  {todoEditingAssignee ? (
                    <form onSubmit={submitTodoAssignee} style={{ display: 'inline-flex', gap: '0.3rem' }}>
                      <input
                        value={todoAssigneeInput}
                        onChange={(e) => setTodoAssigneeInput(e.target.value)}
                        placeholder="username"
                        style={{ fontSize: '0.8rem', padding: '0.1rem 0.3rem', width: 120 }}
                        autoFocus
                      />
                      <button type="submit" style={{ fontSize: '0.75rem', width: 'auto', padding: '0.1rem 0.4rem' }}>ok</button>
                      <button type="button" onClick={() => setTodoEditingAssignee(false)} style={{ fontSize: '0.75rem', background: 'transparent', color: 'var(--color-fg)', width: 'auto', padding: '0.1rem 0.2rem' }}>×</button>
                    </form>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {entry.todo_assignee_username && (
                        <Link to={`/~${entry.todo_assignee_username}`}>~{entry.todo_assignee_username}</Link>
                      )}
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => { setTodoAssigneeInput(entry!.todo_assignee_username ?? ''); setTodoEditingAssignee(true) }}
                          style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.75rem', padding: '0 0.15rem', opacity: 0.5 }}
                        >
                          {entry.todo_assignee_username ? 'edit' : 'assign'}
                        </button>
                      )}
                    </span>
                  )}
                  {isOwner ? (
                    <select
                      value={entry.todo_status ?? 'open'}
                      onChange={(e) => handleTodoStatusChange(e.target.value)}
                      style={{ width: 'auto', fontSize: '0.8rem' }}
                    >
                      {['open', 'wip', 'done', 'cancelled'].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <span>{entry.todo_status ?? 'open'}</span>
                  )}
                </div>
              )}
              <span style={{ opacity: 0.6 }}>[{PRIVACY_LABELS[entry.privacy] ?? entry.privacy}]</span>
            </div>
          </div>
          {entry.signature_valid === false && (
            <p style={{ color: 'var(--color-1)', fontSize: '0.85rem' }}>⚠ signature invalid</p>
          )}
          {attachments.filter((a) => a.mimetype.startsWith('image/') || a.mimetype.startsWith('audio/') || a.mimetype.startsWith('video/')).map((att) => (
            <div key={att.id} style={{ marginBottom: '1rem' }}>
              <AuthenticatedMedia
                url={`/api/v1/entries/${encodeURIComponent(entry.id)}/attachments/${encodeURIComponent(att.id)}`}
                mimetype={att.mimetype}
                name={att.filename}
              />
              {isOwner && (
                <button
                  type="button"
                  onClick={async () => {
                    await deleteAttachment(entry!.id, att.id).catch(() => {})
                    setAttachments((prev) => prev.filter((a) => a.id !== att.id))
                  }}
                  style={{ background: 'none', border: 'none', color: 'var(--color-fg)', cursor: 'pointer', padding: '0 0.2rem', lineHeight: 1, fontSize: '1.2rem', opacity: 0.6, display: 'block', marginTop: '0.25rem' }}
                  aria-label={`remove attachment ${att.filename}`}
                >×</button>
              )}
            </div>
          ))}
          {entry.mimetype.startsWith('text/') ? (
            <div style={bodyStyle}>
              {entry.content && entry.mimetype === 'text/markdown' ? (
                <MarkdownRenderer
                  entryId={entry.id}
                  authorUsername={entry.username}
                  content={entry.content}
                  colorTheme={entry.color_theme}
                  onUpdate={handleMarkdownUpdate}
                />
              ) : entry.content ? (
                <pre>{entry.content}</pre>
              ) : null}
            </div>
          ) : entry.mimetype.startsWith('image/') || entry.mimetype.startsWith('video/') || entry.mimetype.startsWith('audio/') ? (
            <AuthenticatedMedia entryId={entry.id} mimetype={entry.mimetype} name={entry.name || 'untitled'} />
          ) : (
            <button
              type="button"
              disabled={downloading}
              onClick={async () => {
                setDownloading(true)
                await downloadEntry(entry!.id, entry!.name || 'untitled').catch(() => {})
                setDownloading(false)
              }}
              style={{ width: 'auto', marginTop: '0.5rem' }}
            >
              {downloading ? 'downloading…' : `download ${entry.name || 'untitled'} (${entry.mimetype})`}
            </button>
          )}
          {attachments.filter((a) => !a.mimetype.startsWith('image/') && !a.mimetype.startsWith('audio/') && !a.mimetype.startsWith('video/')).map((att) => (
            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={() => downloadAttachment(entry!.id, att.id, att.filename).catch(() => {})}
                style={{ width: 'auto' }}
              >
                download {att.filename} ({att.mimetype})
              </button>
              {isOwner && (
                <button
                  type="button"
                  onClick={async () => {
                    await deleteAttachment(entry!.id, att.id).catch(() => {})
                    setAttachments((prev) => prev.filter((a) => a.id !== att.id))
                  }}
                  style={{ background: 'none', border: 'none', color: 'var(--color-fg)', cursor: 'pointer', padding: '0 0.2rem', lineHeight: 1, fontSize: '1.2rem', opacity: 0.6 }}
                  aria-label={`remove attachment ${att.filename}`}
                >×</button>
              )}
            </div>
          ))}
        </>
      )}

      {!editing && parents.length > 0 && (
        <section style={{ borderTop: '1px solid var(--color-fg)', marginTop: '1.5rem', paddingTop: '1rem' }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', opacity: 0.6 }}>referenced in</h3>
          {parents.map((p) => (
            <div key={p.id} style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
              <Link to={`/~${p.username}/entry/${p.id}`}>{p.name || '(untitled)'}</Link>
              <span style={{ opacity: 0.5, fontSize: '0.8rem', marginLeft: '0.4rem' }}>~{p.username}</span>
            </div>
          ))}
        </section>
      )}

      {!editing && (
        <div style={{ marginTop: '1.5rem' }}>
          {(tags.length > 0 || currentUser) && (
            <div style={{ marginBottom: '0.75rem' }}>
              {currentUser && (
                <TagInput
                  username={currentUser}
                  onSubmit={handleAddTag}
                  excludeTags={tags.filter((t) => t.tagger_username === currentUser).map((t) => t.name)}
                  inputStyle={{ fontSize: '0.85rem' }}
                  buttonStyle={{ width: 'auto', fontSize: '0.85rem' }}
                />
              )}
              {tags.length > 0 && (
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {tags.map((tag) => (
                    <span key={`${tag.tagger_username}:${tag.name}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1rem', background: 'var(--color-fg)', color: 'var(--color-bg)', padding: '0.1rem 0.45rem', borderRadius: 2, fontSize: '0.8rem' }}>
                      <Link to={`/~${tag.tagger_username}/tags/${encodeURIComponent(tag.name)}`} style={{ color: 'inherit', textDecoration: 'none' }}>#{tag.name}</Link>
                      {currentUser === tag.tagger_username && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag.name)}
                          style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.1rem', lineHeight: 1, fontSize: '1rem' }}
                          aria-label={`remove tag ${tag.name}`}
                        >×</button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {isOwner && (
              <button type="button" onClick={startEdit} style={{ width: 'auto', fontSize: '0.85rem' }}>
                edit
              </button>
            )}
            {entry.allow_replies && currentUser && !replying && (!emailEnabled || emailVerified !== false) && (
              <button type="button" onClick={() => setReplying(true)} style={{ width: 'auto', fontSize: '0.85rem' }}>
                reply
              </button>
            )}
          </div>
        </div>
      )}

      {entry.allow_replies && currentUser && emailEnabled && emailVerified === false && (
        <p style={{ marginTop: '1rem', fontSize: '0.85rem', opacity: 0.7 }}>
          <Link to="/settings">verify your email</Link> to post replies.
        </p>
      )}

      {replying && (
        <section style={{ borderTop: '1px solid var(--color-fg)', marginTop: '2rem', paddingTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem' }}>write a reply</h3>
          <EntryEditForm
            name={replyName} onNameChange={setReplyName} namePlaceholder="title"
            content={replyContent} onContentChange={setReplyContent}
            contentRows={6}
            privacy={replyPrivacy} onPrivacyChange={setReplyPrivacy}
            colorTheme={replyColorTheme} onColorThemeChange={setReplyColorTheme}
            username={currentUser}
            saving={submittingReply} submitLabel="post reply" savingLabel="posting…"
            onSubmit={handleSubmitReply}
            onCancel={() => { setReplying(false); setReplyColorTheme(null) }}
          />
        </section>
      )}

      {replies.length > 0 && (
        <section style={{ borderTop: '1px solid var(--color-fg)', marginTop: '2rem', paddingTop: '1rem' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', opacity: 0.7 }}>
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </h3>
          {replies.map((r) => (
            <EntryCard
              key={r.id}
              entry={r}
              hideReplyContext
            />
          ))}
        </section>
      )}
    </article>
  )
}
